// src/pages/Admin/CoursAgenda/hooks/useCoursAgendaAdmin.js

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  addCoursAgenda,
  deleteCoursAgenda,
  getAllCoursAgenda,
  getCoursAgendaById,
  updateCoursAgenda,
} from "../../../../services/coursAgendaServices";

import {
  getAllAssistants,
  getAllEnseignants,
} from "../../../../services/adherentServices";

import {
  getPersonName,
  hydrateCoursAgenda,
  normalizeText,
  sortPeople,
} from "../utils/coursAgendaUtils";

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const toArray = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  return [];
};

/* -------------------------------------------------------------------------- */
/*                                    Hook                                    */
/* -------------------------------------------------------------------------- */

export const useCoursAgendaAdmin = () => {
  /* ------------------------------------------------------------------------ */
  /*                                   State                                  */
  /* ------------------------------------------------------------------------ */

  const [cours, setCours] = useState([]);

  const [enseignants, setEnseignants] = useState([]);

  const [assistants, setAssistants] = useState([]);

  /*
   * loading sert uniquement au PREMIER chargement de la page.
   *
   * Très important :
   * on ne repasse plus loading à true lors d'une création,
   * modification ou suppression.
   *
   * Cela évite de démonter CoursAgendaWeekBoard et donc
   * de perdre la semaine actuellement affichée.
   */
  const [loading, setLoading] = useState(true);

  /*
   * refreshing peut éventuellement être utilisé plus tard
   * si tu veux afficher un petit spinner discret sans remplacer
   * le planning.
   */
  const [refreshing, setRefreshing] =
    useState(false);

  const [error, setError] = useState("");

  const [filters, setFilters] = useState({
    search: "",
    lieu: "",
    enseignantId: "",
    assistantId: "",
  });

  /* ------------------------------------------------------------------------ */
  /*                             Chargement agenda                            */
  /* ------------------------------------------------------------------------ */

  /*
   * showMainLoader :
   *
   * true  = premier chargement de la page
   * false = refresh silencieux après CRUD
   */
  const fetchCoursAgenda = useCallback(
    async (showMainLoader = false) => {
      try {
        if (showMainLoader) {
          setLoading(true);
        } else {
          setRefreshing(true);
        }

        setError("");

        const [
          coursResponse,
          enseignantsResponse,
          assistantsResponse,
        ] = await Promise.all([
          getAllCoursAgenda(),
          getAllEnseignants(),
          getAllAssistants(),
        ]);

        const enseignantsData = sortPeople(
          toArray(enseignantsResponse)
        );

        const assistantsData = sortPeople(
          toArray(assistantsResponse)
        );

        const coursData =
          toArray(coursResponse);

        /*
         * Hydratation des cours :
         * - enseignants_ids
         * - assistants_ids
         * - objets enseignants
         * - objets assistants
         * - couleur du cours
         */
        const hydrated = coursData.map(
          (item) =>
            hydrateCoursAgenda(
              item,
              enseignantsData,
              assistantsData
            )
        );

        setEnseignants(enseignantsData);

        setAssistants(assistantsData);

        setCours(hydrated);
      } catch (err) {
        console.error(
          "Erreur lors du chargement de l'agenda :",
          err
        );

        setError(
          "Impossible de charger l'agenda des cours."
        );
      } finally {
        if (showMainLoader) {
          setLoading(false);
        }

        setRefreshing(false);
      }
    },
    []
  );

  /* ------------------------------------------------------------------------ */
  /*                         Premier chargement uniquement                     */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    /*
     * Ici uniquement on demande le gros loader.
     */
    fetchCoursAgenda(true);
  }, [fetchCoursAgenda]);

  /* ------------------------------------------------------------------------ */
  /*                                  Détail                                  */
  /* ------------------------------------------------------------------------ */

  const getCoursDetails = useCallback(
    async (coursId) => {
      const response =
        await getCoursAgendaById(coursId);

      return hydrateCoursAgenda(
        response,
        enseignants,
        assistants
      );
    },
    [enseignants, assistants]
  );

  /* ------------------------------------------------------------------------ */
  /*                               Création cours                             */
  /* ------------------------------------------------------------------------ */

  const createCours = useCallback(
    async (payload) => {
      await addCoursAgenda(payload);

      /*
       * Refresh silencieux.
       *
       * IMPORTANT :
       * loading ne passe pas à true.
       *
       * Le planning reste monté et conserve donc :
       * - weekOffset
       * - semaine affichée
       */
      await fetchCoursAgenda(false);
    },
    [fetchCoursAgenda]
  );

  /* ------------------------------------------------------------------------ */
  /*                              Modification cours                          */
  /* ------------------------------------------------------------------------ */

  const editCours = useCallback(
    async (coursId, payload) => {
      await updateCoursAgenda(
        coursId,
        payload
      );

      /*
       * Refresh silencieux.
       *
       * C'est cette modification qui corrige ton problème.
       */
      await fetchCoursAgenda(false);
    },
    [fetchCoursAgenda]
  );

  /* ------------------------------------------------------------------------ */
  /*                              Suppression cours                           */
  /* ------------------------------------------------------------------------ */

  const removeCours = useCallback(
    async (coursId) => {
      await deleteCoursAgenda(coursId);

      /*
       * Même principe :
       * on rafraîchit les données sans démonter le planning.
       */
      await fetchCoursAgenda(false);
    },
    [fetchCoursAgenda]
  );

  /* ------------------------------------------------------------------------ */
  /*                              Options lieux                               */
  /* ------------------------------------------------------------------------ */

  const lieuxOptions = useMemo(() => {
    const values = new Set();

    cours.forEach((item) => {
      const lieu =
        item?.lieu?.trim();

      if (lieu) {
        values.add(lieu);
      }
    });

    return Array.from(values).sort(
      (a, b) =>
        a.localeCompare(b, "fr")
    );
  }, [cours]);

  /* ------------------------------------------------------------------------ */
  /*                              Cours filtrés                               */
  /* ------------------------------------------------------------------------ */

  const filteredCours = useMemo(() => {
    const normalizedSearch =
      normalizeText(filters.search);

    return cours.filter((item) => {
      /* -------------------------------------------------------------------- */
      /*                              Recherche                               */
      /* -------------------------------------------------------------------- */

      const enseignantsText = (
        item.enseignants || []
      )
        .map(getPersonName)
        .join(" ");

      const assistantsText = (
        item.assistants || []
      )
        .map(getPersonName)
        .join(" ");

      const searchContent =
        normalizeText(
          [
            item.nom_cours,
            item.theme_cours,
            item.description_cours,
            item.lieu,
            enseignantsText,
            assistantsText,
          ]
            .filter(Boolean)
            .join(" ")
        );

      const searchMatch =
        normalizedSearch
          ? searchContent.includes(
              normalizedSearch
            )
          : true;

      /* -------------------------------------------------------------------- */
      /*                                 Lieu                                 */
      /* -------------------------------------------------------------------- */

      const lieuMatch =
        filters.lieu
          ? normalizeText(
              item.lieu
            ) ===
            normalizeText(
              filters.lieu
            )
          : true;

      /* -------------------------------------------------------------------- */
      /*                              Instructeur                              */
      /* -------------------------------------------------------------------- */

      let enseignantMatch = true;

      if (
        filters.enseignantId ===
        "none"
      ) {
        enseignantMatch =
          !item.enseignants ||
          item.enseignants.length ===
            0;
      } else if (
        filters.enseignantId
      ) {
        enseignantMatch = (
          item.enseignants || []
        ).some(
          (enseignant) =>
            Number(
              enseignant.id
            ) ===
            Number(
              filters.enseignantId
            )
        );
      }

      /* -------------------------------------------------------------------- */
      /*                               Assistant                               */
      /* -------------------------------------------------------------------- */

      let assistantMatch = true;

      if (
        filters.assistantId ===
        "none"
      ) {
        assistantMatch =
          !item.assistants ||
          item.assistants.length === 0;
      } else if (
        filters.assistantId
      ) {
        assistantMatch = (
          item.assistants || []
        ).some(
          (assistant) =>
            Number(
              assistant.id
            ) ===
            Number(
              filters.assistantId
            )
        );
      }

      /* -------------------------------------------------------------------- */

      return (
        searchMatch &&
        lieuMatch &&
        enseignantMatch &&
        assistantMatch
      );
    });
  }, [cours, filters]);

  /* ------------------------------------------------------------------------ */
  /*                              Reset filtres                               */
  /* ------------------------------------------------------------------------ */

  const resetFilters =
    useCallback(() => {
      setFilters({
        search: "",
        lieu: "",
        enseignantId: "",
        assistantId: "",
      });
    }, []);

  /* ------------------------------------------------------------------------ */
  /*                         Nombre filtres actifs                            */
  /* ------------------------------------------------------------------------ */

  const activeFiltersCount =
    useMemo(() => {
      return Object.values(
        filters
      ).filter(
        (value) => value !== ""
      ).length;
    }, [filters]);

  /* ------------------------------------------------------------------------ */
  /*                                   Return                                 */
  /* ------------------------------------------------------------------------ */

  return {
    cours,
    filteredCours,

    enseignants,
    assistants,

    lieuxOptions,

    filters,
    setFilters,
    resetFilters,

    activeFiltersCount,

    /*
     * loading = gros loader initial uniquement
     */
    loading,

    /*
     * refreshing = refresh silencieux après CRUD
     * facultatif pour le moment
     */
    refreshing,

    error,

    fetchCoursAgenda,

    getCoursDetails,

    createCours,
    editCours,
    removeCours,
  };
};